import React, {
  FunctionComponent,
  useContext,
  useState,
  useEffect,
} from "react";
import { FormattedHTMLMessage, FormattedMessage } from "react-intl-phraseapp";
import { Button, Dialog, LockIcon } from "@cmsgov/ds-medicare-gov";
import { makePlanLongId, useTranslate } from "../helpers";
import {
  ActionType,
  Plan,
  PlanType,
  SearchResultPlan,
  PlanEnrollmentStatus,
  PharmacyType,
} from "../@types";
import { AppStore } from "../app/store";
import { useFlags } from "launchdarkly-react-client-sdk";
import { isCurrentPlan } from "../helpers/beneficiaryInfoHelpers";
import { RouteComponentProps, withRouter } from "react-router";
import routes from "../app/routes";
import { getPlanName } from "../helpers/planHelpers";
import { getYearPartInfo } from "../helpers/yearPartHelpers";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
  Ga4Event,
  Ga4EventDimensions,
} from "../app/contexts/Analytics/types";

export interface EnrollOptions extends RouteComponentProps {
  plan: Plan | SearchResultPlan;
  aria_string?: string;
  onClick?: () => void;
  tealiumEventAction?: string;
  buttonClassName?: string;
  dialogClassName?: string;
}

type MissingGa4EventDimensions = Pick<
  Partial<Ga4EventDimensions>,
  "mct_pharmacy_type" | "mct_insurance_provider" | "mct_insurance_type"
>;

const EnrollButtonForm: FunctionComponent<EnrollOptions> = ({
  plan,
  plan: {
    name,
    plan_id,
    contract_id,
    contract_year,
    segment_id,
    low_performing,
    enrollment_status,
  },
  onClick,
  history,
  aria_string,
  tealiumEventAction,
  buttonClassName = "",
  dialogClassName = "",
}) => {
  const flags = useFlags();
  const { feDisableHpmsEnrollment, mctCurrentPlanYear } = flags;

  const { isPublicPreview, isOpenEnrollmentNextYearOnly } = getYearPartInfo(
    flags
  );

  const {
    state: {
      oecStatuses: { currentYear, nextYear },
    },
    state,
    dispatch,
  } = useContext(AppStore.AppContext);
  const [showTransitionModal, setTransitionModalVisibility] = useState(false);
  const t = useTranslate();
  const hideEnrollForCurrentYearPlans =
    isOpenEnrollmentNextYearOnly &&
    plan.contract_year === mctCurrentPlanYear.toString();
  const tealiumPlanType = plan.plan_type === PlanType.PDP ? "PDP" : "MA/MAPD";
  const tealiumEventLabel = `Enroll - ${name} - ${tealiumPlanType}`;

  const pendingEnrollmentPlansLongIds: string[] = [];
  if (currentYear) {
    pendingEnrollmentPlansLongIds.push(makePlanLongId(currentYear));
  }
  if (nextYear) {
    pendingEnrollmentPlansLongIds.push(makePlanLongId(nextYear));
  }
  const currentPlanLongId = makePlanLongId(plan);
  const planIsPendingEnrollment = pendingEnrollmentPlansLongIds.includes(
    currentPlanLongId
  );

  useEffect(() => {
    if (showTransitionModal) {
      const dialogWrapper: HTMLElement | null = document.querySelector(
        ".ds-c-dialog-wrap"
      );

      if (dialogWrapper) {
        dialogWrapper.scrollTop = 0;
      }
    }
  }, [showTransitionModal]);

  if (low_performing) {
    return (
      <p className="ds-u-text-align--center">
        {t("plan_card.contact_plan_to_enroll")}
      </p>
    );
  }

  const openEnrollModal = (): void => {
    setTransitionModalVisibility(true);
  };

  const isCurrPlan = isCurrentPlan(state.currentCoverage, {
    plan_id,
    contract_id,
    contract_year,
    segment_id,
  });

  const isCurrPlanForNextYear = isCurrentPlan(state.nextYearCoverage, {
    plan_id,
    contract_id,
    contract_year,
    segment_id,
  });

  const enrollAnalytics = (): void => {
    const optionalSettings: MissingGa4EventDimensions = {};
    if (!state.selectedPlan) {
      optionalSettings.mct_insurance_provider = plan.name;
      optionalSettings.mct_insurance_type = plan.plan_type;
    }
    if (!state.pharmacyType) {
      optionalSettings.mct_pharmacy_type = PharmacyType.NONE;
    }
    dispatch({
      type: AnalyticsActionType.SEND_GA4_EVENT,
      settings: {
        event_name: Ga4Event.PLAN_ENROLL_CLICKED,
        ...optionalSettings,
      },
    });
    dispatch({
      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
      settings: {
        event_action: tealiumEventAction || "Button",
        event_label: tealiumEventLabel,
      },
    });
  };

  const EnrollButton = (): JSX.Element => {
    if (isCurrPlan && !state.csr) {
      return (
        <p className="mct-c-enroll-button-form__text ds-u-text-align--center">
          {t("plan_card.your_current_plan")}
        </p>
      );
    }

    if (isCurrPlanForNextYear && !state.csr) {
      return (
        <p className="mct-c-enroll-button-form__text ds-u-text-align--center">
          {t("plan_card.your_next_plan")}
        </p>
      );
    }

    const planFrozen = plan.redactions.some(
      r => r.type === "frozen" && r.issue === "frzn_enrlmnt"
    );
    const contactPlan = !plan.enrollment_opt_in_status || planFrozen;

    if (contactPlan) {
      return (
        <p className="mct-c-enroll-button-form__text">
          {t("plan_card.contact_plan_to_enroll")}
        </p>
      );
    } else if (enrollment_status === PlanEnrollmentStatus.NOT_NOW) {
      return (
        <p className="mct-c-enroll-button-form__text">
          {t("plan_enroll.not_accepting")}
        </p>
      );
    }

    if (feDisableHpmsEnrollment) {
      return (
        <p className="mct-c-enroll-button-form__text ds-u-font-style--italic">
          {t("plan_enroll.cannot_process_enrollments")}
        </p>
      );
    }

    return (
      <Button
        type="button"
        variation="solid"
        aria-label={aria_string}
        className={`e2e-plan-enroll mct-hide-print mct-c-enroll-button-form__button ${buttonClassName}`}
        onClick={(): void => {
          if (onClick) {
            onClick();
          }
          dispatch({
            type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
            settings: {
              button: {
                buttonStyle: AnalyticsButtonStyle.PRIMARY,
                buttonType: AnalyticsButtonType.BUTTON,
                text: t("app.terms.enroll"),
              },
            },
          });
          enrollAnalytics();
          openEnrollModal();
        }}
      >
        {t("app.terms.enroll")}
      </Button>
    );
  };

  const onCancel = (): void => {
    setTransitionModalVisibility(false);
    dispatch({
      type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
      settings: {
        button: {
          buttonStyle: AnalyticsButtonStyle.PRIMARY,
          buttonType: AnalyticsButtonType.BUTTON,
          text: t("plan_enroll.cancel"),
        },
      },
    });
    dispatch({
      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
      settings: {
        event_action: tealiumEventAction || "Modal",
        event_label: "Go Back",
      },
    });
  };

  if (planIsPendingEnrollment) {
    return null;
  }

  return (
    <div
      className={`EnrollTransitionDialog PlanCard__mobile_order_2 ${dialogClassName}`}
    >
      {showTransitionModal && (
        <Dialog
          size="wide"
          className="EnrollTransitionDialog__dialog"
          headerClassName="EnrollTransitionDialog__header"
          getApplicationNode={(): HTMLElement | null =>
            document.getElementById("mpf-root")
          }
          onExit={onCancel}
        >
          <div className="EnrollTransitionDialog__content">
            <h1
              className="ds-text-heading--lg ds-u-margin-bottom--0"
              data-cy="enrollment-form-dlg-heading"
            >
              {plan.contract_year == mctCurrentPlanYear + 1 ? (
                <FormattedMessage
                  id="plan_enroll.you_are_joining_with_year"
                  values={{ year: plan.contract_year }}
                />
              ) : (
                t("plan_enroll.you_are_joining")
              )}
            </h1>
            <div className="EnrollTransitionDialog__planDetails">
              <h2
                id="dialog-title"
                className="ds-text-heading--2xl ds-u-margin--0"
              >
                {getPlanName(plan, state.language)}
              </h2>
              <h3 className="ds-text-heading--xl ds-u-margin--0 ds-u-color--gray">
                {`${t(
                  "plan_terms.plan_id"
                )} ${contract_id}-${plan_id}-${segment_id}`}
              </h3>
            </div>
          </div>
          <div className="EnrollTransitionDialog__content">
            <b>{t("enroll_transition.checklist_heading")}:</b>
            <FormattedHTMLMessage
              id={
                plan.contract_year === "2021"
                  ? "enroll_transition.2021.checklist"
                  : "enroll_transition.checklist"
              }
            />
          </div>
          <div className="EnrollTransitionDialog__content ds-u-display--flex ds-u-justify--start ds-u-align-items--center">
            <LockIcon className="ds-u-margin-right--1" />
            <span className="ds-u-font-size--sm ds-u-color--gray">
              {t("enroll_transition.security_advisory.update")}
            </span>
          </div>

          <div className="EnrollTransitionDialog__actions">
            <Button
              className="EnrollTransitionDialog__cancelButton"
              key="cancel"
              onClick={onCancel}
              data-cy="enrollment-form-dlg-cancel-button"
            >
              {t(
                plan.contract_year === "2021"
                  ? "enroll_transition.2021.go_back"
                  : "plan_enroll.cancel"
              )}
            </Button>

            <Button
              variation="solid"
              className="e2e-submit-enrollment-form"
              key="primary"
              onClick={(): void => {
                dispatch({
                  type: ActionType.UPDATE_SELECTED_PLAN,
                  payload: plan,
                });
                history.push(routes.enroll);
                setTransitionModalVisibility(false);
                dispatch({
                  type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                  settings: {
                    event_action: tealiumEventAction || "Modal",
                    event_label: `Join Plan - ${plan.name} - ${tealiumPlanType}`,
                  },
                });
                dispatch({
                  type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                  settings: {
                    button: {
                      buttonStyle: AnalyticsButtonStyle.PRIMARY,
                      buttonType: AnalyticsButtonType.BUTTON,
                      text: t("plan_enroll.join_plan"),
                    },
                    linkUrl: routes.enroll,
                  },
                });
                dispatch({
                  type: AnalyticsActionType.SEND_GA4_EVENT,
                  settings: {
                    event_name: Ga4Event.ENROLLMENT_STARTED,
                  },
                });
              }}
            >
              {t("plan_enroll.join_plan")}
            </Button>
          </div>

          {state.viewMedigap && plan.plan_type === PlanType.PDP && (
            <div className="EnrollTransitionDialog__medigap">
              <h2 className="ds-text-heading--xl">
                {t(
                  plan.contract_year === "2021"
                    ? "enroll_transition.2021.medigap_header"
                    : "enroll_transition.medigap_header"
                )}
              </h2>
              <p>
                {t(
                  plan.contract_year === "2021"
                    ? "enroll_transition.2021.medigap"
                    : "enroll_transition.medigap"
                )}
              </p>
            </div>
          )}
        </Dialog>
      )}

      {isPublicPreview && (
        <>
          {plan.contract_year === (mctCurrentPlanYear + 1).toString() && (
            <p className="e2e-enroll-begins">
              {t("public_preview.enroll_begins")}
            </p>
          )}

          {plan.contract_year === mctCurrentPlanYear.toString() && (
            <EnrollButton />
          )}
        </>
      )}

      {!isPublicPreview && !hideEnrollForCurrentYearPlans && <EnrollButton />}

      {isCurrPlan && (
        <p>
          <FormattedHTMLMessage id="current_plan.current_coverage_disclaimer" />
        </p>
      )}
    </div>
  );
};

export default withRouter(EnrollButtonForm);
